import { useCallback } from 'react';

import { useUpsellingGuard } from '@/shared/plan/useUpsellingGuard';

/**
 * AI 파일/YouTube/Website 첨부 기능에 대한 upselling guard를 제공합니다.
 *
 * 사용자의 plan에 따라 자동으로 upselling 모달을 표시하며,
 * Landing/Manual 구분 없이 plan 기반으로만 동작합니다.
 *
 * @param originalYoutubeHandler - 원본 YouTube 첨부 핸들러
 * @param originalWebsiteHandler - 원본 Website 첨부 핸들러
 * @returns beforeFileAttach, handleYoutube, handleWebsite - AttachmentProvider에 전달할 핸들러들
 *
 * @example
 * ```typescript
 * const { beforeFileAttach, handleYoutube, handleWebsite } = useAttachmentUpsellingGuards(
 *   attachment.handleAttachYoutube,
 *   attachment.handleAttachWebsite
 * );
 *
 * <AttachmentProvider
 *   beforeAttach={beforeFileAttach}
 *   onAttachYoutube={handleYoutube}
 *   onAttachWebsite={handleWebsite}
 * />
 * ```
 */
export const useAttachmentUpsellingGuards = (
  originalYoutubeHandler: () => Promise<void>,
  originalWebsiteHandler: () => Promise<void>,
) => {
  const upsellingGuard = useUpsellingGuard();

  // 파일 첨부 전 guard (plan 체크)
  const beforeFileAttach = useCallback(() => {
    const result = upsellingGuard(
      () => true, // plan 있으면 첨부 허용
      {
        feature: 'AI_FROM_FILE',
        from: 'ai_file_attachment',
      },
    );
    // undefined면 upselling 발동 (첨부 차단)
    return result ?? false;
  }, [upsellingGuard]);

  // YouTube 첨부 handler (plan 체크)
  const handleYoutube = useCallback(async () => {
    void upsellingGuard(originalYoutubeHandler, {
      feature: 'AI_FROM_YOUTUBE',
      from: 'ai_youtube_attachment',
    });
  }, [upsellingGuard, originalYoutubeHandler]);

  // Website 첨부 handler (plan 체크)
  const handleWebsite = useCallback(async () => {
    void upsellingGuard(originalWebsiteHandler, {
      feature: 'AI_FROM_WEBSITE',
      from: 'ai_website_attachment',
    });
  }, [upsellingGuard, originalWebsiteHandler]);

  return {
    beforeFileAttach,
    handleYoutube,
    handleWebsite,
  };
};
